/**
 * Brand Completeness Calculator
 * Determines how complete a brand profile is and what's missing
 */

import type { Brand, BrandDNA } from '../types';

export interface BrandCompletenessResult {
  percentage: number;
  missingFields: MissingField[];
  completedFields: string[];
  level: 'minimal' | 'basic' | 'good' | 'complete';
}

export interface MissingField {
  field: string;
  label: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: 'basic' | 'voice' | 'audience' | 'content';
}

// Weights for different fields (total should equal 100)
const FIELD_WEIGHTS = {
  // Basic Info (25%)
  name: 5,
  description: 5,
  website_url: 5,
  logo_url: 5,
  colors: 5,

  // Brand Voice (30%)
  tone: 10,
  personality_traits: 8,
  writing_style: 7,
  common_phrases: 5,

  // Audience (25%)
  target_audience: 10,
  industry_keywords: 8,
  competitor_differentiation: 7,

  // Content (20%)
  preferred_content_types: 7,
  posting_themes: 7,
  cta_patterns: 6,
};

export function calculateBrandCompleteness(
  brand: Brand,
  brandDNA: Partial<BrandDNA> | null
): BrandCompletenessResult {
  const missingFields: MissingField[] = [];
  const completedFields: string[] = [];
  let totalWeight = 0;

  // Check Basic Info
  if (brand.name) {
    completedFields.push('name');
    totalWeight += FIELD_WEIGHTS.name;
  }

  if (brand.description && brand.description.length > 10) {
    completedFields.push('description');
    totalWeight += FIELD_WEIGHTS.description;
  } else {
    missingFields.push({
      field: 'description',
      label: 'Brand Description',
      description: 'A brief description of what your brand does',
      priority: 'high',
      category: 'basic',
    });
  }

  if (brand.website_url) {
    completedFields.push('website_url');
    totalWeight += FIELD_WEIGHTS.website_url;
  } else {
    missingFields.push({
      field: 'website_url',
      label: 'Website URL',
      description: 'Your brand\'s website for AI analysis',
      priority: 'high',
      category: 'basic',
    });
  }

  if (brand.logo_url) {
    completedFields.push('logo_url');
    totalWeight += FIELD_WEIGHTS.logo_url;
  } else {
    missingFields.push({
      field: 'logo_url',
      label: 'Logo',
      description: 'Upload your brand logo',
      priority: 'medium',
      category: 'basic',
    });
  }

  if (brand.color_primary && brand.color_secondary) {
    completedFields.push('colors');
    totalWeight += FIELD_WEIGHTS.colors;
  }

  // Check Brand DNA fields
  if (brandDNA) {
    // Tone
    if (brandDNA.tone && brandDNA.tone.length >= 2) {
      completedFields.push('tone');
      totalWeight += FIELD_WEIGHTS.tone;
    } else {
      missingFields.push({
        field: 'tone',
        label: 'Brand Tone',
        description: 'How your brand sounds (e.g., professional, friendly, bold)',
        priority: 'high',
        category: 'voice',
      });
    }

    // Personality Traits
    if (brandDNA.personality_traits && brandDNA.personality_traits.length >= 2) {
      completedFields.push('personality_traits');
      totalWeight += FIELD_WEIGHTS.personality_traits;
    } else {
      missingFields.push({
        field: 'personality_traits',
        label: 'Personality Traits',
        description: 'Your brand\'s character traits',
        priority: 'medium',
        category: 'voice',
      });
    }

    // Writing Style
    if (brandDNA.writing_style && brandDNA.writing_style.formality) {
      completedFields.push('writing_style');
      totalWeight += FIELD_WEIGHTS.writing_style;
    } else {
      missingFields.push({
        field: 'writing_style',
        label: 'Writing Style',
        description: 'Formality and vocabulary preferences',
        priority: 'medium',
        category: 'voice',
      });
    }

    // Common Phrases
    if (brandDNA.common_phrases && brandDNA.common_phrases.length >= 1) {
      completedFields.push('common_phrases');
      totalWeight += FIELD_WEIGHTS.common_phrases;
    } else {
      missingFields.push({
        field: 'common_phrases',
        label: 'Brand Phrases',
        description: 'Signature phrases or slogans your brand uses',
        priority: 'low',
        category: 'voice',
      });
    }

    // Target Audience
    if (
      brandDNA.target_audience &&
      (brandDNA.target_audience.demographics?.length > 0 ||
        brandDNA.target_audience.interests?.length > 0)
    ) {
      completedFields.push('target_audience');
      totalWeight += FIELD_WEIGHTS.target_audience;
    } else {
      missingFields.push({
        field: 'target_audience',
        label: 'Target Audience',
        description: 'Who you\'re trying to reach',
        priority: 'high',
        category: 'audience',
      });
    }

    // Industry Keywords
    if (brandDNA.industry_keywords && brandDNA.industry_keywords.length >= 3) {
      completedFields.push('industry_keywords');
      totalWeight += FIELD_WEIGHTS.industry_keywords;
    } else {
      missingFields.push({
        field: 'industry_keywords',
        label: 'Industry Keywords',
        description: 'Key terms related to your industry',
        priority: 'medium',
        category: 'audience',
      });
    }

    // Competitor Differentiation
    if (brandDNA.competitor_differentiation && brandDNA.competitor_differentiation.length > 10) {
      completedFields.push('competitor_differentiation');
      totalWeight += FIELD_WEIGHTS.competitor_differentiation;
    } else {
      missingFields.push({
        field: 'competitor_differentiation',
        label: 'Unique Value',
        description: 'What makes you different from competitors',
        priority: 'medium',
        category: 'audience',
      });
    }

    // Preferred Content Types
    if (brandDNA.preferred_content_types && brandDNA.preferred_content_types.length >= 1) {
      completedFields.push('preferred_content_types');
      totalWeight += FIELD_WEIGHTS.preferred_content_types;
    } else {
      missingFields.push({
        field: 'preferred_content_types',
        label: 'Content Types',
        description: 'Types of content you want to create',
        priority: 'medium',
        category: 'content',
      });
    }

    // Posting Themes
    if (brandDNA.posting_themes && brandDNA.posting_themes.length >= 2) {
      completedFields.push('posting_themes');
      totalWeight += FIELD_WEIGHTS.posting_themes;
    } else {
      missingFields.push({
        field: 'posting_themes',
        label: 'Content Themes',
        description: 'Topics and themes for your content',
        priority: 'medium',
        category: 'content',
      });
    }

    // CTA Patterns
    if (brandDNA.cta_patterns && brandDNA.cta_patterns.length >= 1) {
      completedFields.push('cta_patterns');
      totalWeight += FIELD_WEIGHTS.cta_patterns;
    } else {
      missingFields.push({
        field: 'cta_patterns',
        label: 'Call-to-Actions',
        description: 'How you encourage action from your audience',
        priority: 'low',
        category: 'content',
      });
    }
  } else {
    // No Brand DNA at all - add all DNA fields as missing
    missingFields.push(
      {
        field: 'tone',
        label: 'Brand Tone',
        description: 'How your brand sounds (e.g., professional, friendly, bold)',
        priority: 'high',
        category: 'voice',
      },
      {
        field: 'personality_traits',
        label: 'Personality Traits',
        description: 'Your brand\'s character traits',
        priority: 'medium',
        category: 'voice',
      },
      {
        field: 'target_audience',
        label: 'Target Audience',
        description: 'Who you\'re trying to reach',
        priority: 'high',
        category: 'audience',
      },
      {
        field: 'industry_keywords',
        label: 'Industry Keywords',
        description: 'Key terms related to your industry',
        priority: 'medium',
        category: 'audience',
      },
      {
        field: 'preferred_content_types',
        label: 'Content Types',
        description: 'Types of content you want to create',
        priority: 'medium',
        category: 'content',
      }
    );
  }

  // Sort missing fields by priority
  missingFields.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  // Calculate percentage
  const percentage = Math.round(totalWeight);

  // Determine level
  let level: BrandCompletenessResult['level'];
  if (percentage >= 85) {
    level = 'complete';
  } else if (percentage >= 60) {
    level = 'good';
  } else if (percentage >= 30) {
    level = 'basic';
  } else {
    level = 'minimal';
  }

  return {
    percentage,
    missingFields,
    completedFields,
    level,
  };
}

export function getCompletenessColor(percentage: number): string {
  if (percentage >= 85) return '#22C55E'; // green
  if (percentage >= 60) return '#EAB308'; // yellow
  if (percentage >= 30) return '#F97316'; // orange
  return '#EF4444'; // red
}

export function getCompletenessLabel(level: BrandCompletenessResult['level']): string {
  switch (level) {
    case 'complete':
      return 'Complete';
    case 'good':
      return 'Good';
    case 'basic':
      return 'Basic';
    case 'minimal':
      return 'Needs Setup';
  }
}
